import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  BookOpen,
  AlertTriangle,
  Heart,
  Leaf,
  Recycle,
  Boxes,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city, products = [] }) => {
  const livres = products.find((p) => p.slug === "carton-a-livres");

  return (
    <>
      {/* ======= SECTION 2 — Livraison à domicile ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Votre livraison de cartons à {city.name}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Ne perdez plus de temps dans les zones commerciales à charger
              des liasses d&apos;emballages encombrantes dans un véhicule
              inadapté. Nous prenons en charge l&apos;acheminement complet
              de vos fournitures directement à votre porte, partout à
              {" "}{city.name}.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                title: "Flotte interne et réactive",
                body: "Nous n'externalisons jamais nos livraisons. C'est notre propre société qui charge et dépose vos cartons en Île-de-France. Vous bénéficiez d'une ponctualité rigoureuse et d'un soin maximal lors de la dépose.",
                Icon: Truck,
              },
              {
                title: "Matériel préservé",
                body: "Nos véhicules fermés protègent vos fournitures contre l'humidité et la poussière. Vous recevez des cartons parfaitement rigides, secs et immédiatement prêts à être assemblés.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                    {title}
                  </h3>
                </div>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= SECTION 3 — Module compact pour livres : split éditorial + produit hero ======= */}
      <section className="py-14 md:py-20 bg-white">
        <div className="container-cdm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
            {/* Left: editorial */}
            <div className="lg:col-span-7 flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4 self-start">
                <BookOpen className="h-3.5 w-3.5" strokeWidth={2.6} />
                Préservez vos reliures et votre dos
              </div>
              <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
                Le module compact pour vos livres
              </h2>
              <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
                Vouloir ranger toute sa bibliothèque dans de grands cartons
                pour gagner du temps est un piège logistique majeur. Le
                papier est l&apos;un des matériaux les plus lourds à
                déplacer, et l&apos;utilisation de volumes inadaptés
                engendre deux risques critiques.
              </p>

              <div className="mt-7 space-y-3">
                {[
                  {
                    title: "La rupture par le bas",
                    body: "Sous la pression de dizaines de romans ou de dossiers administratifs empilés, un grand carton subit une force de traction latérale qui fait lâcher les adhésifs. Vos livres risquent de s'éparpiller et de s'abîmer au sol pendant le portage.",
                    Icon: AlertTriangle,
                    accent: "#FF7A00",
                  },
                  {
                    title: "L'ergonomie du levage",
                    body: "Notre carton « petit format » limite mécaniquement le volume de chargement pour que le poids total reste sous la barre des 20 kg. Sa géométrie compacte empêche le tassement des pages et répartit la pression sur les angles renforcés. Vos ouvrages précieux restent intacts, et la manutention se fait sans effort ni risque pour les lombaires.",
                    Icon: Heart,
                    accent: "#237cb5",
                  },
                ].map(({ title, body, Icon, accent }) => (
                  <div
                    key={title}
                    className="flex items-start gap-3 bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5"
                  >
                    <span
                      className="flex items-center justify-center h-11 w-11 rounded-full text-white shrink-0"
                      style={{ backgroundColor: accent }}
                    >
                      <Icon className="h-5 w-5" strokeWidth={2.5} />
                    </span>
                    <div>
                      <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                        {title}
                      </h3>
                      <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                        {body}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: featured book carton hero */}
            <div className="lg:col-span-5 flex">
              {livres && (
                <Link
                  to="/produit/carton-a-livres"
                  className="group relative w-full bg-gradient-to-br from-[#0f1d2b] to-[#1a2d3f] rounded-3xl p-8 md:p-10 text-white overflow-hidden hover:shadow-[0_28px_60px_-26px_rgba(15,29,43,0.55)] transition-all flex flex-col"
                  data-testid="city-ville-davray-cta-livres"
                >
                  <div
                    aria-hidden="true"
                    className="absolute -top-16 -right-16 h-52 w-52 rounded-full bg-[#FF7A00]/25 blur-3xl"
                  />
                  <div
                    aria-hidden="true"
                    className="absolute -bottom-16 -left-16 h-44 w-44 rounded-full bg-[#237cb5]/30 blur-3xl"
                  />

                  <div className="relative">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FF7A00] text-white text-[11px] uppercase tracking-[0.18em] font-bold">
                      <BookOpen className="h-3.5 w-3.5" strokeWidth={2.6} />
                      La référence du segment
                    </div>

                    <h3 className="mt-6 font-display font-extrabold text-3xl md:text-4xl text-white leading-tight">
                      {livres.name}
                    </h3>
                    <p className="mt-2 text-white/65 text-[14.5px]">
                      {livres.dimensions || "Format compact renforcé"}
                    </p>

                    <ul className="mt-7 space-y-3">
                      {[
                        "Poids plafonné à 20 kg",
                        "Angles renforcés double cannelure",
                        "Format ergonomique pour le dos",
                      ].map((item) => (
                        <li
                          key={item}
                          className="flex items-start gap-2.5 text-[14px] text-white/85"
                        >
                          <span className="mt-1 flex items-center justify-center h-5 w-5 rounded-full bg-[#FF7A00] text-white shrink-0">
                            <svg
                              viewBox="0 0 16 16"
                              fill="none"
                              className="h-3 w-3"
                              aria-hidden="true"
                            >
                              <path
                                d="M3 8.5l3 3 7-7"
                                stroke="currentColor"
                                strokeWidth="2.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="relative mt-8 flex items-end justify-between pt-6 border-t border-white/15">
                    <div>
                      <div className="font-display font-extrabold text-3xl text-white">
                        {Number(livres.price).toFixed(2).replace(".", ",")} €
                      </div>
                      <div className="text-white/60 text-[12.5px]">
                        par carton — livré chez vous
                      </div>
                    </div>
                    <span className="inline-flex items-center justify-center h-11 w-11 rounded-full bg-[#FF7A00] text-white group-hover:translate-x-1 transition-transform">
                      <ArrowRight className="h-5 w-5" strokeWidth={2.6} />
                    </span>
                  </div>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ======= CTA Big Déménagement (au milieu) ======= */}
      <section className="py-12 bg-white">
        <div className="container-cdm">
          <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
        </div>
      </section>

      {/* ======= SECTION 4 — Engagement éco-conçu (bleu charte) ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#cfdbe6] text-[11px] uppercase tracking-[0.18em] font-bold text-[#237cb5] mb-4">
              <Leaf className="h-3.5 w-3.5" strokeWidth={2.6} />
              Engagement éco-conçu
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Des emballages 100&nbsp;% recyclables
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que la transition vers un nouveau lieu de vie doit se
              faire dans le respect des ressources, notre gamme
              s&apos;inscrit pleinement dans une démarche d&apos;économie
              circulaire.
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              {
                kicker: "Filière responsable",
                title: "Fibres naturelles revalorisées",
                body: "Nos cartons proviennent de filières responsables et de forêts gérées durablement. Ils s'intègrent parfaitement dans l'écosystème local de gestion des déchets.",
                Icon: Leaf,
              },
              {
                kicker: "Cycle de vie",
                title: "Valorisation totale après usage",
                body: "Après votre déménagement, nos produits sont intégralement recyclables. Vous pouvez les plier à plat pour les réutiliser, les transmettre à un proche ou les déposer dans les bacs de tri sélectif pour qu'ils redeviennent de la matière première, réduisant ainsi au maximum l'empreinte environnementale de votre départ.",
                Icon: Recycle,
              },
            ].map(({ kicker, title, body, Icon }) => (
              <article
                key={title}
                className="bg-white rounded-2xl border border-[#cfdbe6] p-6 md:p-7 flex flex-col"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-6 w-6" strokeWidth={2.4} />
                  </span>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#237cb5]">
                      {kicker}
                    </div>
                    <h3 className="mt-1 font-display font-extrabold text-[17px] text-[#0f1d2b] leading-tight">
                      {title}
                    </h3>
                  </div>
                </div>
                <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </article>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/cartons-reconditionnes"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-ville-davray-cta-reco"
            >
              <Recycle className="h-4 w-4" strokeWidth={2.6} />
              Voir nos cartons reconditionnés
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
            <Link
              to="/cartons-demenagement"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
              data-testid="city-ville-davray-cta-shop"
            >
              <Boxes className="h-4 w-4" strokeWidth={2.6} />
              Voir tous les cartons
            </Link>
          </div>
        </div>
      </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-ville-davray-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
    </>
  );
};

export default { Sections };
